import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export type Skills = Record<
  string,
  {
    tot: number;
    conosco: number;
    non_conosco: number;
    label: string;
  }
>;

export type Likes = Record<
  string,
  {
    tot: number;
    like: number;
    dislike: number;
    label: string;
  }
>;


export type EduSkills = Record<
  string,
  {
    teacher: number;
    student: number;
    label: string;
  }
>;

export type UploadFileList = Record<
  string,
  {
    team: string;
    members: string;
    date: string;
  }
>;

export interface TeamData {
  teams: {
    id: number;
    name: string;
    active: boolean;
  };
}

export interface UserKnowledge {
  id: number;
  knowledge: number;
  like: number;
  teaching_learning: number;
  date: Date;
  user: UserInfo;
  skill: {
    id: number;
    name: string;
    description: string;
  };
}

export interface TeamSkillEntry {
  skill: string;
  teams: string[];
}

export interface TeamSkillData {
  activeTeamCount: number;
  data: TeamSkillEntry[];
}

export interface UserInfo {
  name: string;
  acronym: string;
  image: string | null;
  active?: boolean;
  teams?: TeamData;
}

export interface YearlySkillsDto {
  year: number;
  user: UserInfo;
  skills: Record<string, SkillDto>;
}

export interface SkillDto {
  name?: string;
  group: string | null;
  knowledge: number | null;
  like: number | null;
  teaching_learning: number | null;
  date: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);

  getAll(): Observable<Skills> {
    return this.http.get<Skills>('/api/v1/chart');
  }

  getLearning(): Observable<EduSkills> {
    return this.http.get<EduSkills>('/api/v1/chart-learning');
  }
  
  getLikes(): Observable<Likes> {
    return this.http.get<Likes>('/api/v1/chart-likes');
  }

  uploadFile(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<string>('/api/v1/upload', formData);
  }

  getUploadedFiles(): Observable<UploadFileList> {
    return this.http.get<UploadFileList>('/api/v1/upload/list');
  }

  getSkillUserKnowledge(skillName: string): Observable<UserKnowledge[]> {
    return this.http
      .get<UserKnowledge[]>(`/api/v1/users/${skillName}`)
      .pipe(map(list => list.filter(el => el.knowledge === 1)));
  }

  getSkillUserLikes(skillName: string): Observable<UserKnowledge[]> {
    return this.http
      .get<UserKnowledge[]>(`/api/v1/users/${skillName}`)
      .pipe(map(list => list.filter(el => el.like != 3)));
  }

  getSkillUserLearning(skillName: string): Observable<UserKnowledge[]> {
    return this.http
      .get<UserKnowledge[]>(`/api/v1/users/${skillName}`)
      .pipe(map(list => list.filter(el => el.teaching_learning !== null)));
  }

createTeam(teamName: string): Observable<TeamData> {
    return this.http.post<TeamData>('/api/v1/teams/create', { name: teamName });
  }   

  getTeamsWithSkills(): Observable<
    { name: string; skills: { name: string }[] }[]
  > {
    return this.http.get<{ name: string; skills: { name: string }[] }[]>(
      '/api/v1/skill-team/team-with-skills'
    );
  }

  getSkillTeamMap(): Observable<TeamSkillData> {
    return this.http.get<TeamSkillData>('/api/v1/skill-team/skills-with-teams');
  }

  updateSkillsForTeam(teamName: string, skillNames: string[]) {
    return this.http.post(`/api/v1/skill-team/update-skills`, {
      teamName,
      skillNames,
    });
  }

  getAllTeams(): Observable<TeamData[]> {
    return this.http.get<TeamData[]>('/api/v1/teams');
  }

  updateTeamActiveStatus(teamId: number, active: boolean): Observable<any> {
    return this.http.patch(`/api/v1/teams/${teamId}/active`, { active });
  }

  deleteUpload(team: string, date: Date) {
    return this.http.delete(`/api/v1/upload?team=${encodeURIComponent(team)}&date=${encodeURIComponent(date.toISOString())}`);
  }

  deleteTeam(teamId: number) {
  return this.http.delete(`/api/v1/teams/${teamId}`);
}

getTeamMatches(teamId: number) {
  return this.http.get<any[]>(`/api/v1/teams/match/${teamId}`);
}

getUserImage(userName: string): Observable<string> {
  // Replace the URL with your actual endpoint
  return this.http.get<{ imageUrl: string }>(`/api/v1/users/${userName}/image`)
    .pipe(
      map(response => response.imageUrl),
      catchError(() => of('assets/default-user.png'))
    );
}

getTeamMembers(teamId: number) {
  return this.http.get<string[]>(`/api/teams/${teamId}/members`);
}
}
